import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Routines } from '@heidi/design-system/icons';
import { toast } from '@heidi/design-system/toast';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { cn } from '@heidi/utils';
import {
  Copy,
  CopyPlus,
  Ellipsis,
  History,
  Maximize2,
  Pencil,
  Pin,
  Share2,
  Trash2,
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

interface ProjectChatMenuProps {
  agentRunId?: string;
  onRename?: () => void;
  isSaved?: boolean;
  onToggleSaved?: () => void;
  showMakeAsWorkflow?: boolean;
  onMakeAsWorkflow?: () => void;
  /** Renders a destructive Delete item last when supplied; omitted entirely otherwise. */
  onDelete?: () => void;
  /** Hide rename / pin / delete / create-routine. Copy chat ID and chat history stay. */
  readOnly?: boolean;
  showShare?: boolean;
  showDuplicate?: boolean;
  onShare?: () => void;
  onDuplicate?: () => void;
  onOpenHistory?: () => void;
  /** Session panel: leave the sidebar and open this chat full screen. */
  onOpenFullscreen?: () => void;
}

export interface ProjectChatMenuItemsProps {
  agentRunId?: string;
  onRename?: () => void;
  isSaved?: boolean;
  onToggleSaved?: () => void;
  showMakeAsWorkflow?: boolean;
  onMakeAsWorkflow?: () => void;
  /** Renders a destructive Delete item last when supplied; omitted entirely otherwise. */
  onDelete?: () => void;
  readOnly?: boolean;
  showShare?: boolean;
  showDuplicate?: boolean;
  onShare?: () => void;
  onDuplicate?: () => void;
  onOpenHistory?: () => void;
  /** Session panel: leave the sidebar and open this chat full screen. */
  onOpenFullscreen?: () => void;
}

export const ProjectChatMenuItems = ({
  agentRunId,
  onRename,
  isSaved = false,
  onToggleSaved,
  showMakeAsWorkflow = false,
  onMakeAsWorkflow,
  onDelete,
  readOnly = false,
  showShare = false,
  showDuplicate = false,
  onShare,
  onDuplicate,
  onOpenHistory,
  onOpenFullscreen,
}: ProjectChatMenuItemsProps) => {
  const intl = useIntl();
  const copyRunId = useCallback(async () => {
    if (!agentRunId) return;
    const copiedTitle = intl.formatMessage({
      defaultMessage: 'Chat ID copied',
      id: 'iaZ6+/ImrJ',
      description: 'Toast shown after copying the chat id (the agent run id)',
    });
    try {
      await navigator.clipboard.writeText(agentRunId);
      toast({ title: copiedTitle, tone: 'success' });
    } catch {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Could not copy to clipboard',
          id: 'UTH5OHkyZb',
          description: 'Toast shown when copying an id from the project chat menu fails',
        }),
        tone: 'destructive',
      });
    }
  }, [agentRunId, intl]);

  const saveLabel = isSaved
    ? intl.formatMessage({
        defaultMessage: 'Unpin',
        id: 'vEYDrnkdgg',
        description: 'Project chat overflow menu: unpin the chat',
      })
    : intl.formatMessage({
        defaultMessage: 'Pin',
        id: 'iJElSF5jA0',
        description: 'Project chat overflow menu: pin the chat',
      });

  return (
    <>
      {showShare && onShare && !readOnly && (
        <DropdownMenuItem onSelect={onShare}>
          <Share2 aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Share',
            id: 'O7u/cySiLJ',
            description: 'Project chat overflow menu: share the chat',
          })}
        </DropdownMenuItem>
      )}
      {showDuplicate && onDuplicate && (
        <DropdownMenuItem onSelect={onDuplicate}>
          <CopyPlus aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Duplicate to my Work',
            id: 'FumapxNmcx',
            description: 'Project chat overflow menu: fork the chat into the user Work',
          })}
        </DropdownMenuItem>
      )}
      {!readOnly && onRename && (
        <DropdownMenuItem onSelect={onRename}>
          <Pencil aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Rename',
            id: 'izIGlNOR8K',
            description: 'Project chat overflow menu: rename the chat',
          })}
        </DropdownMenuItem>
      )}
      {!readOnly && onToggleSaved && (
        <DropdownMenuItem onSelect={onToggleSaved}>
          <Pin className={cn(isSaved && 'fill-current')} aria-hidden />
          {saveLabel}
        </DropdownMenuItem>
      )}
      {onOpenFullscreen && (
        <DropdownMenuItem onSelect={onOpenFullscreen}>
          <Maximize2 aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Open full screen',
            id: 'CFBBShmmo+',
            description: 'Session Work panel: expand the project to its full page',
          })}
        </DropdownMenuItem>
      )}
      {!readOnly && showMakeAsWorkflow && onMakeAsWorkflow && (
        <DropdownMenuItem onSelect={onMakeAsWorkflow}>
          <Routines aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Create routine from chat',
            id: 'IJKbU5z0+O',
            description: 'Project chat overflow menu: turn chat into a routine',
          })}
        </DropdownMenuItem>
      )}
      {onOpenHistory && (
        <DropdownMenuItem onSelect={onOpenHistory}>
          <History aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Chat history',
            id: 'tza7eZyH7O',
            description: 'Chat overflow menu: open recent and saved chats',
          })}
        </DropdownMenuItem>
      )}
      {agentRunId && (
        <DropdownMenuItem onSelect={() => void copyRunId()}>
          <Copy aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Copy chat ID',
            id: 'ivwHgNqFmI',
            description:
              'Project chat overflow menu: copy the chat id (the agent run id)',
          })}
        </DropdownMenuItem>
      )}
      {onDelete && !readOnly && (
        <DropdownMenuItem variant="destructive" onSelect={onDelete}>
          <Trash2 aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Delete',
            id: 'p5c7fga9ot',
            description: 'Project chat overflow menu: delete the chat',
          })}
        </DropdownMenuItem>
      )}
    </>
  );
};

export const ProjectChatMenu = ({
  agentRunId,
  onRename,
  isSaved = false,
  onToggleSaved,
  showMakeAsWorkflow = false,
  onMakeAsWorkflow,
  onDelete,
  readOnly = false,
  showShare = false,
  showDuplicate = false,
  onShare,
  onDuplicate,
  onOpenHistory,
  onOpenFullscreen,
}: ProjectChatMenuProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const justClosedRef = useRef(false);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setTooltipOpen(false);
      return;
    }

    justClosedRef.current = true;
    setTooltipOpen(false);
    setTimeout(() => {
      justClosedRef.current = false;
    }, 150);
  }, []);

  const handleTooltipOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && (open || justClosedRef.current)) return;
      setTooltipOpen(nextOpen);
    },
    [open]
  );

  const moreLabel = intl.formatMessage({
    defaultMessage: 'More actions',
    id: 'a8/rvIlUsX',
    description: 'Project chat header: aria-label/tooltip for the overflow menu button',
  });

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <Tooltip open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="text-text-primary"
              aria-label={moreLabel}
              data-testid="project-chat-menu-trigger"
            >
              <Ellipsis aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {moreLabel}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={4}
        // Keep focus from bouncing back to the trigger on close — that would
        // blur the rename input (mounted by Rename) and immediately commit.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <ProjectChatMenuItems
          agentRunId={agentRunId}
          onRename={onRename}
          isSaved={isSaved}
          onToggleSaved={onToggleSaved}
          showMakeAsWorkflow={showMakeAsWorkflow}
          onMakeAsWorkflow={onMakeAsWorkflow}
          onDelete={onDelete}
          readOnly={readOnly}
          showShare={showShare}
          showDuplicate={showDuplicate}
          onShare={onShare}
          onDuplicate={onDuplicate}
          onOpenHistory={onOpenHistory}
          onOpenFullscreen={onOpenFullscreen}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
